import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import BaseInput from "../BaseInput";
import { StaticDatePicker, StaticTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { getLanguageMeta } from "@i18n";
import { useTopModalZIndex } from "@hooks/useModalZIndex";
import { CalendarToday, AccessTime } from "@mui/icons-material";
import SuggestionButton from "../components/SuggestionButton";
import "../styles.scss";

// Format date for display: dd/mm/aaaa
const formatDateDisplay = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, "").slice(0, 8);

    if (digits.length <= 2) {
        return digits;
    } else if (digits.length <= 4) {
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }
};

// Format time for display: hh:mm
const formatTimeDisplay = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, "").slice(0, 4);

    if (digits.length <= 2) {
        return digits;
    } else {
        return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    }
};

// Validate date
const isValidDate = (day, month, year) => {
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);

    if (isNaN(d) || isNaN(m) || isNaN(y)) return false;
    if (d < 1 || d > 31) return false;
    if (m < 1 || m > 12) return false;
    if (y < 1 || y > 9999) return false;

    const daysInMonth = new Date(y, m, 0).getDate();
    return d <= daysInMonth;
};

// Validate time
const isValidTime = (hours, minutes) => {
    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (isNaN(h) || isNaN(m)) return false;
    if (h < 0 || h > 23) return false;
    if (m < 0 || m > 59) return false;

    return true;
};

// Convert external format (yyyy-mm-dd hh:mm:ss) to display format
const convertExternalToDisplay = (externalDateTime) => {
    if (!externalDateTime) return { date: "", time: "" };

    const [datePart, timePart] = externalDateTime.split(" ");

    if (!datePart) return { date: "", time: "" };

    const [year, month, day] = datePart.split("-");
    const dateDisplay = (year && month && day && isValidDate(day, month, year))
        ? `${day}/${month}/${year}`
        : "";

    let timeDisplay = "";
    if (timePart) {
        const [hours, minutes] = timePart.split(":");
        timeDisplay = (hours && minutes && isValidTime(hours, minutes))
            ? `${hours}:${minutes}`
            : "";
    }

    return { date: dateDisplay, time: timeDisplay };
};

// Convert display format to external format (yyyy-mm-dd hh:mm:00)
const convertDisplayToExternal = (dateDisplay, timeDisplay) => {
    if (!dateDisplay) return "";

    const dateDigits = dateDisplay.replace(/\D/g, "");
    if (dateDigits.length < 8) return "";

    const day = dateDigits.slice(0, 2);
    const month = dateDigits.slice(2, 4);
    const year = dateDigits.slice(4, 8);

    if (!isValidDate(day, month, year)) return "";

    const timeDigits = timeDisplay ? timeDisplay.replace(/\D/g, "") : "";
    let hours = "00";
    let minutes = "00";

    if (timeDigits.length >= 2) {
        hours = timeDigits.slice(0, 2);
        if (timeDigits.length >= 4) {
            minutes = timeDigits.slice(2, 4);
        }
    }

    if (!isValidTime(hours, minutes)) {
        hours = "00";
        minutes = "00";
    }

    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
};

// Backdrop styles for click-away detection
const backdropStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
    backgroundColor: "transparent",
};

// Picker container styles
const pickerContainerStyles = {
    position: "absolute",
    zIndex: 9999,
    marginTop: "8px",
    backgroundColor: "var(--surface-9)",
    border: "1px solid var(--surface-8)",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    overflow: "hidden",
};

const DateTimeInput = ({
    value,
    onInput,
    label,
    placeholder,
    variant = "light",
    allowFuture = true,
    allowToday = true,
    allowPast = true,
    suggestions,
    className,
    ...otherProps
}) => {
    const { t } = useTranslation("fragments");
    const resolvedLabel = label ?? t("input.dateTime.label");
    const resolvedPlaceholder = placeholder ?? t("input.dateTime.datePlaceholder");
    const pickerLocale = getLanguageMeta().dayjs;
    const { date: initialDate, time: initialTime } = convertExternalToDisplay(value);
    const [dateValue, setDateValue] = useState(initialDate);
    const [timeValue, setTimeValue] = useState(initialTime);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [timePickerOpen, setTimePickerOpen] = useState(false);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0, openAbove: false });
    const dateInputRef = useRef(null);
    const timeInputRef = useRef(null);
    const dateContainerRef = useRef(null);
    const timeContainerRef = useRef(null);
    const timeSelectionCount = useRef(0); // Track hour/minute selection steps

    // Estimated picker heights for positioning calculation
    const CALENDAR_HEIGHT = 340;
    const TIME_PICKER_HEIGHT = 280;
    const PICKER_GAP = 8;

    // Pickers portal to <body>; they must float above whatever layer hosts this
    // input — the topmost modal, or a floating panel on top of it (which uses
    // this same base) — so the backdrop sits one step above that layer and the
    // picker one more.
    const layerZ = useTopModalZIndex();

    // The last value this input reported. While typing, a half-filled date or
    // time reports "" (incomplete); the parent echoes it back as `value`, and
    // re-seeding from that echo would wipe what the user is typing.
    const lastEmittedRef = useRef(value);
    const emit = (next) => {
        lastEmittedRef.current = next;
        if (onInput) onInput(next);
    };

    // Sync internal state when external value changes (e.g., from suggestions)
    useEffect(() => {
        if (value === lastEmittedRef.current) return;
        lastEmittedRef.current = value;
        const { date, time } = convertExternalToDisplay(value);
        setDateValue(date);
        setTimeValue(time);
    }, [value]);

    // Calculate picker position based on input element and available screen space
    const updatePickerPosition = (containerRef, pickerHeight) => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            // Check if there's enough space below
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            // Determine if we should open above or below
            const openAbove = spaceBelow < pickerHeight + PICKER_GAP && spaceAbove > spaceBelow;

            // Calculate top position
            let top;
            if (openAbove) {
                // Position above the input
                top = rect.top + window.scrollY - pickerHeight - PICKER_GAP;
            } else {
                // Position below the input
                top = rect.bottom + window.scrollY + PICKER_GAP;
            }

            // Calculate left position, ensuring it doesn't go off-screen
            let left = rect.left + window.scrollX;
            const pickerWidth = 320; // Estimated picker width
            if (left + pickerWidth > viewportWidth) {
                left = viewportWidth - pickerWidth - 16; // 16px margin from edge
            }
            if (left < 16) {
                left = 16;
            }

            setPickerPosition({ top, left, openAbove });
        }
    };

    const handleDateChange = (newValue) => {
        const digits = newValue.replace(/\D/g, "").slice(0, 8);
        const displayValue = formatDateDisplay(digits);
        setDateValue(displayValue);

        const externalValue = convertDisplayToExternal(displayValue, timeValue);
        emit(externalValue);
    };

    const handleTimeChange = (newValue) => {
        const digits = newValue.replace(/\D/g, "").slice(0, 4);
        const displayValue = formatTimeDisplay(digits);
        setTimeValue(displayValue);

        const externalValue = convertDisplayToExternal(dateValue, displayValue);
        emit(externalValue);
    };

    // Only one picker at a time: both share `pickerPosition`.
    const handleDateFocus = () => {
        setTimePickerOpen(false);
        updatePickerPosition(dateContainerRef, CALENDAR_HEIGHT);
        setCalendarOpen(true);
    };

    const handleTimeFocus = () => {
        setCalendarOpen(false);
        timeSelectionCount.current = 0;
        updatePickerPosition(timeContainerRef, TIME_PICKER_HEIGHT);
        setTimePickerOpen(true);
    };

    const handleCalendarIconClick = (e) => {
        e.stopPropagation();
        if (!calendarOpen) {
            setTimePickerOpen(false);
            updatePickerPosition(dateContainerRef, CALENDAR_HEIGHT);
        }
        setCalendarOpen(!calendarOpen);
    };

    const handleTimeIconClick = (e) => {
        e.stopPropagation();
        if (!timePickerOpen) {
            setCalendarOpen(false);
            timeSelectionCount.current = 0;
            updatePickerPosition(timeContainerRef, TIME_PICKER_HEIGHT);
        }
        setTimePickerOpen(!timePickerOpen);
    };

    const handleCalendarChange = (newDate) => {
        if (newDate && newDate.isValid()) {
            const day = String(newDate.date()).padStart(2, "0");
            const month = String(newDate.month() + 1).padStart(2, "0");
            const year = newDate.year();
            const displayValue = `${day}/${month}/${year}`;
            setDateValue(displayValue);

            const externalValue = convertDisplayToExternal(displayValue, timeValue);
            emit(externalValue);
        }
        // Close calendar immediately after selection
        setCalendarOpen(false);
    };

    const handleTimePickerChange = (newTime) => {
        if (newTime && newTime.isValid()) {
            const hours = String(newTime.hour()).padStart(2, "0");
            const minutes = String(newTime.minute()).padStart(2, "0");
            const displayValue = `${hours}:${minutes}`;
            setTimeValue(displayValue);

            const externalValue = convertDisplayToExternal(dateValue, displayValue);
            emit(externalValue);

            // Increment selection count and close after second selection (minute)
            timeSelectionCount.current += 1;
            if (timeSelectionCount.current >= 2) {
                setTimePickerOpen(false);
                timeSelectionCount.current = 0;
            }
        }
    };

    const handleTimePickerAccept = (newTime) => {
        if (newTime && newTime.isValid()) {
            const hours = String(newTime.hour()).padStart(2, "0");
            const minutes = String(newTime.minute()).padStart(2, "0");
            const displayValue = `${hours}:${minutes}`;
            setTimeValue(displayValue);

            const externalValue = convertDisplayToExternal(dateValue, displayValue);
            emit(externalValue);
        }
        setTimePickerOpen(false);
        timeSelectionCount.current = 0;
    };

    // Close picker when backdrop is clicked
    const handleBackdropClick = (pickerType) => {
        if (pickerType === "calendar") {
            setCalendarOpen(false);
        } else if (pickerType === "time") {
            setTimePickerOpen(false);
            timeSelectionCount.current = 0;
        }
    };

    // Parse current date value for calendar
    const getCalendarValue = () => {
        if (!dateValue) return null;

        const digits = dateValue.replace(/\D/g, "");
        if (digits.length < 8) return null;

        const day = digits.slice(0, 2);
        const month = digits.slice(2, 4);
        const year = digits.slice(4, 8);

        if (!isValidDate(day, month, year)) return null;

        return dayjs(`${year}-${month}-${day}`);
    };

    // Parse current time value for time picker
    const getTimeValue = () => {
        if (!timeValue) return null;

        const digits = timeValue.replace(/\D/g, "");
        if (digits.length < 4) return null;

        const hours = digits.slice(0, 2);
        const minutes = digits.slice(2, 4);

        if (!isValidTime(hours, minutes)) return null;

        return dayjs().hour(parseInt(hours, 10)).minute(parseInt(minutes, 10));
    };

    // Determine min/max dates based on props
    const getMinDate = () => {
        if (!allowPast) return dayjs();
        return undefined;
    };

    const getMaxDate = () => {
        if (!allowFuture) return dayjs();
        return undefined;
    };

    const shouldDisableDate = (date) => {
        if (!allowToday && date.isSame(dayjs(), 'day')) return true;
        return false;
    };

    // Render calendar picker with backdrop using Portal
    const renderCalendarPicker = () => {
        if (!calendarOpen) return null;

        return createPortal(
            <>
                {/* Backdrop - clicking this closes the picker */}
                <div
                    style={{ ...backdropStyles, zIndex: layerZ + 1 }}
                    onClick={() => handleBackdropClick("calendar")}
                />
                {/* Picker container */}
                <div
                    style={{
                        ...pickerContainerStyles,
                        zIndex: layerZ + 2,
                        top: pickerPosition.top,
                        left: pickerPosition.left,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={pickerLocale}>
                        <StaticDatePicker
                            value={getCalendarValue()}
                            onChange={handleCalendarChange}
                            minDate={getMinDate()}
                            maxDate={getMaxDate()}
                            shouldDisableDate={shouldDisableDate}
                            displayStaticWrapperAs="desktop"
                            slotProps={{
                                actionBar: { actions: [] },
                            }}
                            sx={{
                                backgroundColor: "transparent",
                                "& .MuiPickersDay-root": {
                                    color: "var(--text-1)",
                                    "&:hover": {
                                        backgroundColor: "var(--surface-8)",
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: "var(--primary-6)",
                                        color: "black",
                                        "&:hover": {
                                            backgroundColor: "var(--primary-8)",
                                            color: "black",
                                        },
                                    },
                                },
                                "& .MuiPickersCalendarHeader-label": {
                                    color: "var(--text-1)",
                                },
                                "& .MuiPickersCalendarHeader-switchViewButton": {
                                    color: "var(--text-1)",
                                },
                                "& .MuiPickersArrowSwitcher-button": {
                                    color: "var(--text-1)",
                                },
                                "& .MuiDayCalendar-weekDayLabel": {
                                    color: "var(--surface-4)",
                                },
                                "& .MuiPickersYear-yearButton": {
                                    color: "var(--text-1)",
                                    "&.Mui-selected": {
                                        backgroundColor: "var(--primary-6)",
                                        color: "black",
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </div>
            </>,
            document.body
        );
    };

    // Render time picker with backdrop using Portal
    const renderTimePicker = () => {
        if (!timePickerOpen) return null;

        return createPortal(
            <>
                {/* Backdrop - clicking this closes the picker */}
                <div
                    style={{ ...backdropStyles, zIndex: layerZ + 1 }}
                    onClick={() => handleBackdropClick("time")}
                />
                {/* Picker container */}
                <div
                    style={{
                        ...pickerContainerStyles,
                        zIndex: layerZ + 2,
                        top: pickerPosition.top,
                        left: pickerPosition.left,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={pickerLocale}>
                        <StaticTimePicker
                            value={getTimeValue()}
                            onChange={handleTimePickerChange}
                            onAccept={handleTimePickerAccept}
                            ampm={false}
                            views={['hours', 'minutes']}
                            minutesStep={5}
                            displayStaticWrapperAs="desktop"
                            slotProps={{
                                actionBar: { actions: [] },
                            }}
                            sx={{
                                backgroundColor: "transparent",
                                "& .MuiMultiSectionDigitalClock-root": {
                                    "& .MuiMultiSectionDigitalClockSection-root": {
                                        scrollbarWidth: "none",
                                        "&::-webkit-scrollbar": {
                                            display: "none",
                                        },
                                    },
                                    "& .MuiMenuItem-root": {
                                        color: "var(--text-1)",
                                        "&:hover": {
                                            backgroundColor: "var(--surface-8)",
                                        },
                                        "&.Mui-selected": {
                                            backgroundColor: "var(--primary-6)",
                                            color: "black",
                                            "&:hover": {
                                                backgroundColor: "var(--primary-8)",
                                            },
                                        },
                                    },
                                },
                                "& .MuiDigitalClock-root": {
                                    "& .MuiDigitalClock-item": {
                                        color: "var(--text-1)",
                                        "&:hover": {
                                            backgroundColor: "var(--surface-8)",
                                        },
                                        "&.Mui-selected": {
                                            backgroundColor: "var(--primary-6)",
                                            color: "black",
                                            "&:hover": {
                                                backgroundColor: "var(--primary-8)",
                                            },
                                        },
                                    },
                                },
                                "& .MuiPickersArrowSwitcher-button": {
                                    color: "var(--text-1)",
                                },
                                "& .MuiTypography-root": {
                                    color: "var(--text-1)",
                                },
                            }}
                        />
                    </LocalizationProvider>
                </div>
            </>,
            document.body
        );
    };

    return (
        <>
            <div className={className} style={{ position: "relative", width: "100%" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: "8px",
                        width: "100%",
                    }}
                >
                    {/* Date Input */}
                    <div style={{ width: "180px", position: "relative" }} ref={dateContainerRef}>
                        <BaseInput
                            ref={dateInputRef}
                            value={dateValue}
                            onInput={handleDateChange}
                            onFocus={handleDateFocus}
                            label={resolvedLabel}
                            placeholder={resolvedPlaceholder}
                            variant={variant}
                            maxLength={10}
                            end={
                                <span
                                    onClick={handleCalendarIconClick}
                                    style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                >
                                    <CalendarToday style={{ fontSize: "18px" }} />
                                </span>
                            }
                        />
                    </div>

                    {/* Time Input */}
                    <div style={{ width: "165px", position: "relative" }} ref={timeContainerRef}>
                        <BaseInput
                            ref={timeInputRef}
                            value={timeValue}
                            onInput={handleTimeChange}
                            onFocus={handleTimeFocus}
                            label=" "
                            placeholder="hh:mm"
                            variant={variant}
                            maxLength={5}
                            start={
                                <span style={{ display: "flex", alignItems: "center", color: "var(--surface-5)" }}>
                                    {t("input.dateTime.at")}
                                </span>
                            }
                            end={
                                <span
                                    onClick={handleTimeIconClick}
                                    style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                >
                                    <AccessTime style={{ fontSize: "18px" }} />
                                </span>
                            }
                        />
                    </div>
                </div>
                {suggestions && suggestions.length > 0 && (
                    <div className="input-suggestions">
                        {suggestions.map((suggestion, index) => (
                            <SuggestionButton
                                key={index}
                                suggestion={suggestion}
                                onClick={onInput}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Render pickers via Portal */}
            {renderCalendarPicker()}
            {renderTimePicker()}
        </>
    );
};

export default DateTimeInput;
